import { BookMarked, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import type { LessonPlan } from '../lib/types';
import { prettyDay } from '../ui/PlannerCalendar';
import { RichTextEditor, RichTextView } from '../ui/RichText';
import { Sheet } from '../ui/Sheet';
import { SubjectField } from '../ui/SubjectField';
import { useParent } from './ParentContext';

// Lesson plans (#186): a parent's daily plan for a child, on the planner calendar. Parent only.

export const lessonTitle = (p: LessonPlan) => [p.subject, p.topic].filter(Boolean).join(' — ') || 'Lesson plan';

/** A lesson plan's row on the calendar (day popup, week and day views). */
export function LessonPlanRow({ plan, onOpen, onChanged }: { plan: LessonPlan; onOpen: () => void; onChanged: () => void }) {
  const { toast } = useParent();
  async function remove() {
    if (!confirm(`Delete the lesson plan “${lessonTitle(plan)}”?`)) return;
    await api.del(`/api/lesson-plans/${plan.id}`).then(onChanged).catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }
  const materialsLeft = plan.materials.filter((m) => !m.done).length;
  return (
    <div className="planner-item lesson-row">
      <button className="lesson-open" onClick={onOpen}>
        <BookMarked size={18} className="lesson-icon" />
        <span className="grow">
          <strong>{lessonTitle(plan)}</strong>
          <span className="tag" style={{ marginLeft: 6 }}>
            Lesson plan
          </span>
          <span className="muted small block">
            {plan.objectives.length} objective{plan.objectives.length === 1 ? '' : 's'}
            {plan.materials.length > 0 && ` · ${materialsLeft ? `${materialsLeft} material${materialsLeft === 1 ? '' : 's'} to gather` : 'materials ready'}`}
          </span>
        </span>
      </button>
      <span className="row-gap planner-actions">
        <button className="btn small" onClick={onOpen}>
          Open
        </button>
        <button className="btn small danger-text" onClick={remove} title="Delete" aria-label="Delete lesson plan">
          <Trash2 size={14} />
        </button>
      </span>
    </div>
  );
}

/** Read a plan; tick materials off as they're gathered; switch to editing. */
export function LessonPlanSheet({ plan, onClose, onChanged }: { plan: LessonPlan; onClose: () => void; onChanged: () => void }) {
  const { toast } = useParent();
  const [current, setCurrent] = useState(plan);
  const [editing, setEditing] = useState(false);

  async function tick(i: number, done: boolean) {
    try {
      setCurrent(await api.post<LessonPlan>(`/api/lesson-plans/${current.id}/materials/${i}`, { done }));
      onChanged();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  if (editing) {
    return (
      <LessonPlanForm
        childId=""
        plan={current}
        onClose={() => setEditing(false)}
        onSaved={(p) => {
          setCurrent(p);
          setEditing(false);
          onChanged();
        }}
      />
    );
  }

  return (
    <Sheet title={lessonTitle(current)} onClose={onClose}>
      <div className="stack lesson-view">
        <div className="row-between">
          <span className="muted">{prettyDay(current.date)}</span>
          <button className="btn small" onClick={() => setEditing(true)}>
            <Pencil size={14} /> Edit
          </button>
        </div>
        <section>
          <h3>Lesson focus and goals</h3>
          <RichTextView html={current.focusHtml} />
        </section>
        <section>
          <h3>Materials needed</h3>
          {current.materials.length === 0 ? (
            <p className="muted small">—</p>
          ) : (
            current.materials.map((m, i) => (
              <label key={i} className="check">
                <input type="checkbox" checked={m.done} onChange={(e) => tick(i, e.target.checked)} />
                <span className={m.done ? 'muted strike' : ''}>{m.text}</span>
              </label>
            ))
          )}
        </section>
        <section>
          <h3>Learning objectives</h3>
          {current.objectives.length === 0 ? (
            <p className="muted small">—</p>
          ) : (
            <ul className="lesson-objectives">
              {current.objectives.map((o, i) => (
                <li key={i}>{o}</li>
              ))}
            </ul>
          )}
        </section>
        <section>
          <h3>Structure / activity</h3>
          <RichTextView html={current.activityHtml} />
        </section>
        <section>
          <h3>Assessment</h3>
          <RichTextView html={current.assessmentHtml} />
        </section>
      </div>
    </Sheet>
  );
}

/** Create or edit a plan. */
export function LessonPlanForm({
  childId,
  date,
  plan,
  onClose,
  onSaved,
}: {
  childId: string;
  date?: string;
  plan?: LessonPlan;
  onClose: () => void;
  onSaved: (p: LessonPlan) => void;
}) {
  const { toast } = useParent();
  const [subject, setSubject] = useState(plan?.subject ?? '');
  const [day, setDay] = useState(plan?.date ?? date ?? '');
  const [topic, setTopic] = useState(plan?.topic ?? '');
  const [focusHtml, setFocusHtml] = useState(plan?.focusHtml ?? '');
  const [materials, setMaterials] = useState(plan?.materials.length ? plan.materials : [{ text: '', done: false }]);
  const [objectives, setObjectives] = useState(plan?.objectives.length ? plan.objectives : ['']);
  const [activityHtml, setActivityHtml] = useState(plan?.activityHtml ?? '');
  const [assessmentHtml, setAssessmentHtml] = useState(plan?.assessmentHtml ?? '');
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      const body = { date: day, subject: subject.trim(), topic: topic.trim(), focusHtml, materials, objectives, activityHtml, assessmentHtml };
      const saved = plan
        ? await api.put<LessonPlan>(`/api/lesson-plans/${plan.id}`, body)
        : await api.post<LessonPlan>(`/api/children/${childId}/lesson-plans`, body);
      onSaved(saved);
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title={plan ? 'Edit lesson plan' : 'New lesson plan'} onClose={onClose}>
      <div className="stack lesson-form">
        <div className="field-row">
          <label>
            Subject
            <SubjectField value={subject} onChange={setSubject} />
          </label>
          <label>
            Date
            <input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          </label>
        </div>
        <label>
          Topic
          <input value={topic} onChange={(e) => setTopic(e.target.value)} maxLength={200} placeholder="e.g. Adding fractions" />
        </label>
        <RichTextEditor label="Lesson focus and goals" value={focusHtml} onChange={setFocusHtml} placeholder="What this lesson is about and what you want to achieve" />

        <div className="stack">
          <div className="richtext-label">Materials needed</div>
          {materials.map((m, i) => (
            <div key={i} className="row-gap lesson-list-row">
              <input type="checkbox" checked={m.done} onChange={(e) => setMaterials((l) => l.map((x, j) => (j === i ? { ...x, done: e.target.checked } : x)))} aria-label="Gathered" />
              <input className="grow" value={m.text} maxLength={200} placeholder="e.g. Fraction strips" onChange={(e) => setMaterials((l) => l.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
              <button className="icon-btn" aria-label="Remove material" onClick={() => setMaterials((l) => l.filter((_, j) => j !== i))}>
                <X size={15} />
              </button>
            </div>
          ))}
          <button className="btn small" onClick={() => setMaterials((l) => [...l, { text: '', done: false }])}>
            <Plus size={14} /> Add material
          </button>
        </div>

        <div className="stack">
          <div className="richtext-label">Learning objectives</div>
          {objectives.map((o, i) => (
            <div key={i} className="row-gap lesson-list-row">
              <span className="lesson-bullet">•</span>
              <input className="grow" value={o} maxLength={300} placeholder="Students will be able to…" onChange={(e) => setObjectives((l) => l.map((x, j) => (j === i ? e.target.value : x)))} />
              <button className="icon-btn" aria-label="Remove objective" onClick={() => setObjectives((l) => l.filter((_, j) => j !== i))}>
                <X size={15} />
              </button>
            </div>
          ))}
          <button className="btn small" onClick={() => setObjectives((l) => [...l, ''])}>
            <Plus size={14} /> Add objective
          </button>
        </div>

        <RichTextEditor label="Structure / activity" value={activityHtml} onChange={setActivityHtml} placeholder="How the lesson will run" />
        <RichTextEditor label="Assessment" value={assessmentHtml} onChange={setAssessmentHtml} placeholder="How you'll check understanding" minHeight={80} />
        <button className="btn primary" disabled={busy || !day || (!subject.trim() && !topic.trim())} onClick={save}>
          {plan ? 'Save' : 'Add lesson plan'}
        </button>
      </div>
    </Sheet>
  );
}
